/**
 * MediaPipe-based teleoperation device (TypeScript version).
 * Extracts human SEW (Shoulder, Elbow, Wrist) coordinates and wrist poses from camera stream.
 * Works in browser using @mediapipe/pose and @mediapipe/hands.
 *
 * MediaPipe 3D Coordinate System (right-handed):
 *   - Origin: Midpoint of subject's hips (world landmarks reference)
 *   - X-axis: Positive points to subject's right
 *   - Y-axis: Positive points downwards
 *   - Z-axis: Positive points away from camera (deeper into scene)
 *
 * Body-centric coordinate frame (used for robot control):
 *   - Origin: Shoulder center (for upper body tracking)
 *   - Y-axis: Right to left (shoulder line)
 *   - Z-axis: Up direction (opposite of torso vector)
 *   - X-axis: Forward direction (cross product Y × Z)
 *
 * Robot body-centric frame (final output):
 *     +X - front
 *     +Y - left
 *     +Z - up
 * For both hands/wrists, when in zero config (arm by body side), follows the same coordinates as above.
 *
 * Author: Chuizheng Kong and Yunho Cho
 * Date created: 2025-09-12
 */

import {
  PoseLandmarker,
  HandLandmarker,
  FilesetResolver,
  type PoseLandmarkerResult,
  type HandLandmarkerResult
} from '@mediapipe/tasks-vision';
import { Matrix, SingularValueDecomposition } from 'ml-matrix';
import { mat3, vec3 } from 'gl-matrix';
import { recordExternalIkSolveDuration } from '$lib/stores/ik';
import { loadSewMimicModule, type SewMimicModule } from '$lib/sew-mimic/runtime';

// MediaPipe Pose landmark indices
const POSE_LANDMARKS = {
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_PINKY: 17,
  RIGHT_PINKY: 18,
  LEFT_INDEX: 19,
  RIGHT_INDEX: 20,
  LEFT_THUMB: 21,
  RIGHT_THUMB: 22,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32
} as const;

// MediaPipe Hand landmark indices
const HAND_LANDMARKS = {
  WRIST: 0,
  THUMB_CMC: 1,
  THUMB_MCP: 2,
  THUMB_IP: 3,
  THUMB_TIP: 4,
  INDEX_FINGER_MCP: 5,
  INDEX_FINGER_PIP: 6,
  INDEX_FINGER_DIP: 7,
  INDEX_FINGER_TIP: 8,
  MIDDLE_FINGER_MCP: 9,
  MIDDLE_FINGER_PIP: 10,
  MIDDLE_FINGER_DIP: 11,
  MIDDLE_FINGER_TIP: 12,
  RING_FINGER_MCP: 13,
  RING_FINGER_PIP: 14,
  RING_FINGER_DIP: 15,
  RING_FINGER_TIP: 16,
  PINKY_MCP: 17,
  PINKY_PIP: 18,
  PINKY_DIP: 19,
  PINKY_TIP: 20
} as const;

interface SEWPose {
  S: vec3 | null;
  E: vec3 | null;
  W: vec3 | null;
}

interface SEWPoses {
  left: SEWPose;
  right: SEWPose;
  R_world_body?: mat3 | null;
}

interface WristPoses {
  left: mat3 | null;
  right: mat3 | null;
}

export interface HandData {
  landmarks: { [key: string]: vec3 } | null;
  confidence: number;
}

export interface HandPoses {
  left: HandData;
  right: HandData;
}

export interface ControllerState {
  sew_poses: SEWPoses;
  wrist_poses: WristPoses;
  hand_poses: HandPoses;
  torso_angles: vec3 | null;
  engaged: boolean;
  g1_solution?: {
    left_arm: number[];
    right_arm: number[];
    torso?: number[] | null;
    control_commands: number[];
    metadata?: unknown;
  } | null;
  finger_positions_wrist_frame?: {
    left?: Record<string, { mcp?: number[]; pip?: number[]; tip?: number[] }> | null;
    right?: Record<string, { mcp?: number[]; pip?: number[]; tip?: number[] }> | null;
  };
}

interface BodyFrame {
  origin: vec3;
  x_axis: vec3;
  y_axis: vec3;
  z_axis: vec3;
  R_world_body: mat3;
}

export class MediaPipeTeleopDevice {
  private poseLandmarker: PoseLandmarker | undefined;
  private handLandmarker: HandLandmarker | undefined;
  private sewMimicModule: SewMimicModule | null = null;
  private mirrorActions: boolean;
  private debug: boolean;
  private engaged: boolean;

  // State tracking
  private humanSewPoses!: SEWPoses;
  private humanWristPoses!: WristPoses;
  private humanHandPoses!: HandPoses;
  private controllerState: ControllerState | null;
  private latestPoseResults: PoseLandmarkerResult | null = null;
  private latestHandResults: HandLandmarkerResult | null = null;

  // Pre-allocated vectors and matrices for memory efficiency
  private _tempVec3_1: vec3 = vec3.create();
  private _tempVec3_2: vec3 = vec3.create();
  private _tempVec3_3: vec3 = vec3.create();
  private _tempVec3_4: vec3 = vec3.create();
  private _tempVec3_5: vec3 = vec3.create();
  private _tempMat3_1: mat3 = mat3.create();
  private _tempMat3_2: mat3 = mat3.create();

  // Constant rotation matrix
  private readonly R_STD_CAM: mat3 = mat3.fromValues(
    0, 0, -1,
    1, 0, 0,
    0, -1, 0
  );

  // Standard camera rotation matrix
  // private R_std_cam: mat3; // Removed in favor of R_STD_CAM constant

  constructor(options: {
    debug?: boolean;
    mirrorActions?: boolean;
  } = {}) {
    this.debug = options.debug ?? false;
    this.mirrorActions = options.mirrorActions ?? false;
    this.engaged = false;
    this.controllerState = null;

    // Standard camera rotation matrix (equivalent to scipy Rotation from original Python)
    // this.R_std_cam = mat3.create();
    // ZYX Euler angles: [π/2, 0, -π/2]
    // const rotZ = mat3.fromRotation(mat3.create(), Math.PI / 2);
    // const rotY = mat3.fromRotation(mat3.create(), 0);
    // const rotX = mat3.fromRotation(mat3.create(), -Math.PI / 2);
    // mat3.multiply(this.R_std_cam, rotZ, rotY);
    // mat3.multiply(this.R_std_cam, this.R_std_cam, rotX);
    // Replaced by constant R_STD_CAM

    this._resetInternalState();
    // this._displayControls();
    this._initializeMediaPipe();
  }

  private async _initializeMediaPipe(): Promise<void> {
    try {
      this.sewMimicModule = await loadSewMimicModule();

      const vision = await FilesetResolver.forVisionTasks('/wasm');

      // Initialize pose landmarker
      this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      // Initialize hand landmarker
      this.handLandmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numHands: 2,
        minHandDetectionConfidence: 0.5,
        minHandPresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

    } catch (error) {
      if (this.debug) {
        console.error('Error initializing MediaPipe:', error);
      }
    }
  }

  private _displayControls(): void {
    console.log("MediaPipe Teleoperation Device Controls:");
    console.log("- Raise both arms to shoulder height: Start pose tracking");
    console.log("- Lower arms: Stop pose tracking");
  }

  private _resetInternalState(): void {
    this.humanSewPoses = {
      left: { S: null, E: null, W: null },
      right: { S: null, E: null, W: null },
      R_world_body: null
    };
    this.humanWristPoses = {
      left: null,
      right: null
    };
    this.humanHandPoses = {
      left: { landmarks: null, confidence: 0.0 },
      right: { landmarks: null, confidence: 0.0 }
    };
    this.engaged = false;
  }

  async processFrame(videoElement: HTMLVideoElement): Promise<void> {
    if (!this.poseLandmarker || !this.handLandmarker || !this.sewMimicModule) {
      return; // Models not ready yet
    }

    const startTimeMs = performance.now();

    // Process pose detection
    const poseResults = this.poseLandmarker.detectForVideo(videoElement, startTimeMs);
    this.latestPoseResults = poseResults;

    // Process hand detection
    const handResults = this.handLandmarker.detectForVideo(videoElement, startTimeMs);
    this.latestHandResults = handResults;

    this._processFrameWithRust(poseResults, handResults);
  }

  private _processFrameWithRust(poseResults: PoseLandmarkerResult, handResults: HandLandmarkerResult): void {
    if (!this.sewMimicModule) {
      return;
    }

    try {
      const hands = (handResults.worldLandmarks && handResults.handedness)
        ? handResults.worldLandmarks.map((worldLandmarks, idx) => ({
            label: handResults.handedness?.[idx]?.[0]?.categoryName ?? 'unknown',
            score: handResults.handedness?.[idx]?.[0]?.score ?? 0.0,
            world_landmarks: worldLandmarks.map((lm) => ({ x: lm.x, y: lm.y, z: lm.z }))
          }))
        : [];

      const frameInput = {
        pose_world_landmarks: poseResults.worldLandmarks?.[0]?.map((lm) => ({ x: lm.x, y: lm.y, z: lm.z })) ?? [],
        pose_visibility: poseResults.landmarks?.[0]?.map((lm) => lm.visibility ?? 0.0) ?? [],
        hands,
        solve_ik: true,
        mirror_actions: this.mirrorActions
      };

      const solveStart = performance.now();
      const output = (this.sewMimicModule as unknown as {
        g1_process_mediapipe_frame: (frame: unknown) => unknown;
      }).g1_process_mediapipe_frame(frameInput) as {
        controller_state?: {
          sew_poses?: {
            left?: { S?: number[] | null; E?: number[] | null; W?: number[] | null };
            right?: { S?: number[] | null; E?: number[] | null; W?: number[] | null };
            R_world_body?: number[] | null;
          };
          wrist_poses?: { left?: number[] | null; right?: number[] | null };
          hand_poses?: {
            left?: { landmarks?: Record<string, number[]> | null; confidence?: number };
            right?: { landmarks?: Record<string, number[]> | null; confidence?: number };
          };
          torso_angles?: number[] | null;
          engaged?: boolean;
        };
        g1_solution?: {
          left_arm: number[];
          right_arm: number[];
          torso?: number[] | null;
          control_commands: number[];
          metadata?: unknown;
        } | null;
        finger_positions_wrist_frame?: {
          left?: Record<string, { mcp?: number[]; pip?: number[]; tip?: number[] }> | null;
          right?: Record<string, { mcp?: number[]; pip?: number[]; tip?: number[] }> | null;
        };
      };

      recordExternalIkSolveDuration(performance.now() - solveStart);

      const state = output.controller_state;
      if (!state?.sew_poses) {
        return;
      }

      const toVec3 = (v?: number[] | null): vec3 | null => {
        if (!v || v.length < 3) return null;
        return vec3.fromValues(v[0], v[1], v[2]);
      };

      const toMat3 = (m?: number[] | null): mat3 | null => {
        if (!m || m.length < 9) return null;
        return mat3.fromValues(m[0], m[1], m[2], m[3], m[4], m[5], m[6], m[7], m[8]);
      };

      const convertLandmarks = (landmarks?: Record<string, number[]> | null): { [key: string]: vec3 } | null => {
        if (!landmarks) return null;
        const converted: { [key: string]: vec3 } = {};
        for (const [key, value] of Object.entries(landmarks)) {
          const v = toVec3(value);
          if (v) converted[key] = v;
        }
        return Object.keys(converted).length > 0 ? converted : null;
      };

      this.humanSewPoses = {
        left: {
          S: toVec3(state.sew_poses.left?.S),
          E: toVec3(state.sew_poses.left?.E),
          W: toVec3(state.sew_poses.left?.W)
        },
        right: {
          S: toVec3(state.sew_poses.right?.S),
          E: toVec3(state.sew_poses.right?.E),
          W: toVec3(state.sew_poses.right?.W)
        },
        R_world_body: toMat3(state.sew_poses.R_world_body)
      };

      this.humanWristPoses = {
        left: toMat3(state.wrist_poses?.left),
        right: toMat3(state.wrist_poses?.right)
      };

      this.humanHandPoses = {
        left: {
          landmarks: convertLandmarks(state.hand_poses?.left?.landmarks),
          confidence: state.hand_poses?.left?.confidence ?? 0.0
        },
        right: {
          landmarks: convertLandmarks(state.hand_poses?.right?.landmarks),
          confidence: state.hand_poses?.right?.confidence ?? 0.0
        }
      };

      this.engaged = Boolean(state.engaged);

      this.controllerState = {
        sew_poses: this._cloneSewPoses(this.humanSewPoses),
        wrist_poses: this._cloneWristPoses(this.humanWristPoses),
        hand_poses: this._cloneHandPoses(this.humanHandPoses),
        torso_angles: toVec3(state.torso_angles),
        engaged: this.engaged,
        g1_solution: output.g1_solution ?? null,
        finger_positions_wrist_frame: output.finger_positions_wrist_frame
      };
    } catch (error) {
      if (this.debug) {
        console.error('Error processing landmarks in Rust WASM pipeline:', error);
      }
      // Fallback to the previous TS implementation if Rust processing fails.
      this._processPoseResults(poseResults);
      this._processHandResults(handResults);
    }
  }

  private _processPoseResults(results: PoseLandmarkerResult): void {
    // Store latest results for drawing
    this.latestPoseResults = results;

    try {
      if (results.landmarks && results.worldLandmarks && results.landmarks.length > 0) {
        // Get body-centric coordinates
        const bodyCentricCoords = this._getBodyCentricCoordinates(results);

        if (bodyCentricCoords) {
          // Check visibility using normalized landmarks
          const landmarks = results.landmarks[0]; // First pose
          const leftShoulder = landmarks[POSE_LANDMARKS.LEFT_SHOULDER];
          const leftElbow = landmarks[POSE_LANDMARKS.LEFT_ELBOW];
          const leftWrist = landmarks[POSE_LANDMARKS.LEFT_WRIST];

          const rightShoulder = landmarks[POSE_LANDMARKS.RIGHT_SHOULDER];
          const rightElbow = landmarks[POSE_LANDMARKS.RIGHT_ELBOW];
          const rightWrist = landmarks[POSE_LANDMARKS.RIGHT_WRIST];

          // Check visibility (same threshold as Python version)
          const leftSewValid = [leftShoulder, leftElbow, leftWrist].every(lm => lm.visibility && lm.visibility > 0.05);
          const rightSewValid = [rightShoulder, rightElbow, rightWrist].every(lm => lm.visibility && lm.visibility > 0.05);

          // Update SEW poses only if landmarks are visible
          if (leftSewValid && bodyCentricCoords.left) {
            this.humanSewPoses.left = bodyCentricCoords.left;
          } else {
            this.humanSewPoses.left = { S: null, E: null, W: null };
          }

          if (rightSewValid && bodyCentricCoords.right) {
            this.humanSewPoses.right = bodyCentricCoords.right;
          } else {
            this.humanSewPoses.right = { S: null, E: null, W: null };
          }

          if (leftSewValid && rightSewValid && bodyCentricCoords.body_frame) {
            this.humanSewPoses.R_world_body = bodyCentricCoords.body_frame.R_world_body;
          } else {
            this.humanSewPoses.R_world_body = null;
          }

          this._checkEngagement();
        } else {
          // No body-centric coordinates - reset poses
          this.humanSewPoses.left = { S: null, E: null, W: null };
          this.humanSewPoses.right = { S: null, E: null, W: null };
          this.humanSewPoses.R_world_body = null;
          this._checkEngagement();
        }
      } else {
        // No pose landmarks - reset poses
        this.humanSewPoses.left = { S: null, E: null, W: null };
        this.humanSewPoses.right = { S: null, E: null, W: null };
        this.humanSewPoses.R_world_body = null;
        this._checkEngagement();
      }

      // Update controller state
      // Use shallow copies where possible to reduce GC pressure
      let torsoAngles: vec3 | null = null;
      if (this.humanSewPoses.R_world_body) {
        torsoAngles = this._computeTorsoAngles(this.humanSewPoses.R_world_body);
      }

      this.controllerState = {
        sew_poses: this._cloneSewPoses(this.humanSewPoses),
        wrist_poses: this._cloneWristPoses(this.humanWristPoses),
        hand_poses: this._cloneHandPoses(this.humanHandPoses),
        torso_angles: torsoAngles,
        engaged: this.engaged
      };

    } catch (error) {
      if (this.debug) {
        console.error("Error processing pose landmarks:", error);
      }
    }
  }

  private _processHandResults(results: HandLandmarkerResult): void {
    // Clear stale hand data when no hands detected
    if (!results.landmarks || !results.handedness || results.landmarks.length === 0) {
      this.humanWristPoses.left = null;
      this.humanWristPoses.right = null;
      this.humanHandPoses.left = { landmarks: null, confidence: 0.0 };
      this.humanHandPoses.right = { landmarks: null, confidence: 0.0 };
      return;
    }

    try {
      if (results.landmarks && results.handedness && results.worldLandmarks) {
        // Get body-centric coordinates for reference (need current pose state)
        // For now, we'll assume this is called after pose processing
        const handCentricCoords = this._getHandCentricCoordinates(results);

        // Compute wrist rotations from hand landmarks
        if (handCentricCoords) {
          for (const side of ['left', 'right'] as const) {
            if (handCentricCoords[side]) {
              const wristRotation = this._computeWristRotationFromHand(side, handCentricCoords[side].landmarks);
              if (wristRotation) {
                this.humanWristPoses[side] = wristRotation;
              }

              // Store hand landmarks for debugging
              this.humanHandPoses[side] = {
                landmarks: handCentricCoords[side].landmarks,
                confidence: handCentricCoords[side].confidence || 0.0
              };
            }
          }
        }
      }
    } catch (error) {
      if (this.debug) {
        console.error("Error processing hand landmarks:", error);
      }
    }
  }

  private _transformToBodyFrame(landmark: { x: number; y: number; z: number }): vec3 {
    // Reuse _tempVec3_2 for worldPos
    vec3.set(this._tempVec3_2, landmark.x, landmark.y, landmark.z);

    // Reuse _tempVec3_4 for relativePos
    // _tempVec3_3 holds bodyOrigin
    vec3.subtract(this._tempVec3_4, this._tempVec3_2, this._tempVec3_3);

    // Apply transformation: rotation_matrix.T @ relative_pos
    // _tempMat3_1 holds rotationMatrix
    const robotCoords = vec3.create();
    vec3.transformMat3(robotCoords, this._tempVec3_4, this._tempMat3_1);

    return robotCoords;
  }

  private _getBodyCentricCoordinates(poseResults: PoseLandmarkerResult): {
    left: SEWPose;
    right: SEWPose;
    body_frame: BodyFrame;
  } | null {
    if (!poseResults.worldLandmarks || poseResults.worldLandmarks.length === 0) {
      return null;
    }

    const landmarks3d = poseResults.worldLandmarks[0]; // First pose

    // Get key body landmarks
    const leftShoulder = landmarks3d[POSE_LANDMARKS.LEFT_SHOULDER];
    const rightShoulder = landmarks3d[POSE_LANDMARKS.RIGHT_SHOULDER];
    const leftHip = landmarks3d[POSE_LANDMARKS.LEFT_HIP];
    const rightHip = landmarks3d[POSE_LANDMARKS.RIGHT_HIP];

    // Calculate body center (midpoint between shoulders and hips)
    // Use _tempVec3_1 for shoulderCenter
    vec3.set(this._tempVec3_1,
      (leftShoulder.x + rightShoulder.x) / 2,
      (leftShoulder.y + rightShoulder.y) / 2,
      (leftShoulder.z + rightShoulder.z) / 2
    );

    // Use _tempVec3_2 for hipCenter
    vec3.set(this._tempVec3_2,
      (leftHip.x + rightHip.x) / 2,
      (leftHip.y + rightHip.y) / 2,
      (leftHip.z + rightHip.z) / 2
    );

    // Use _tempVec3_3 for bodyOrigin (clone of shoulderCenter)
    vec3.copy(this._tempVec3_3, this._tempVec3_1);

    // Create body-centric coordinate frame
    // Based on user observations, we need to map MediaPipe coordinates correctly:
    // MediaPipe: X+ = subject's right, Y+ = down, Z+ = away from camera
    // Robot: X+ = front, Y+ = left, Z+ = up

    // Robot Y-axis (left): MediaPipe X+ direction (subject's right to left)
    // Use _tempVec3_4 for robotYAxis
    vec3.set(this._tempVec3_4,
      leftShoulder.x - rightShoulder.x,
      leftShoulder.y - rightShoulder.y,
      leftShoulder.z - rightShoulder.z
    );
    vec3.normalize(this._tempVec3_4, this._tempVec3_4);

    // Robot Z-axis (up): opposite of MediaPipe Y+ (up is negative of down)
    // torsoVector = hipCenter - shoulderCenter
    // Use _tempVec3_5 for torsoVector
    vec3.subtract(this._tempVec3_5, this._tempVec3_2, this._tempVec3_1);
    // robotZAxis = -torsoVector
    // Reuse _tempVec3_5 for robotZAxis
    vec3.negate(this._tempVec3_5, this._tempVec3_5);
    vec3.normalize(this._tempVec3_5, this._tempVec3_5);

    // Robot X-axis (front): cross product Y × Z
    // Reuse _tempVec3_1 for robotXAxis (shoulderCenter no longer needed)
    vec3.cross(this._tempVec3_1, this._tempVec3_4, this._tempVec3_5);
    vec3.normalize(this._tempVec3_1, this._tempVec3_1);

    // Create rotation matrix: [X_robot, Y_robot, Z_robot] (columns are robot axes)
    // We construct the Transpose directly to get World->Body transformation
    // The matrix constructed here is [X0 X1 X2; Y0 Y1 Y2; Z0 Z1 Z2]
    // Use _tempMat3_1
    mat3.set(this._tempMat3_1,
      this._tempVec3_1[0], this._tempVec3_4[0], this._tempVec3_5[0],
      this._tempVec3_1[1], this._tempVec3_4[1], this._tempVec3_5[1],
      this._tempVec3_1[2], this._tempVec3_4[2], this._tempVec3_5[2]
    );

    const R_world_body_corrected = mat3.create();
    mat3.multiply(R_world_body_corrected, this.R_STD_CAM, this._tempMat3_1);

    // Extract SEW coordinates in body-centric frame
    const sewCoordinates = {
      left: {
        S: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.LEFT_SHOULDER]),
        E: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.LEFT_ELBOW]),
        W: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.LEFT_WRIST])
      },
      right: {
        S: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.RIGHT_SHOULDER]),
        E: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.RIGHT_ELBOW]),
        W: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.RIGHT_WRIST])
      },
      body_frame: {
        origin: vec3.clone(this._tempVec3_3), // Clone to persist
        x_axis: vec3.clone(this._tempVec3_1), // Clone to persist
        y_axis: vec3.clone(this._tempVec3_4), // Clone to persist (wait, _tempVec3_4 was reused!)
        z_axis: vec3.clone(this._tempVec3_5), // Clone to persist
        R_world_body: R_world_body_corrected
      }
    };
    
    // Fix for y_axis: _tempVec3_4 was reused in transformToBodyFrame.
    // We need to re-compute or save it if we want to return it in body_frame.
    // However, body_frame.y_axis is not strictly used in the current logic except for debugging maybe?
    // Let's recompute it to be safe.
    // y_axis was: leftShoulder - rightShoulder normalized.
    vec3.set(sewCoordinates.body_frame.y_axis,
      leftShoulder.x - rightShoulder.x,
      leftShoulder.y - rightShoulder.y,
      leftShoulder.z - rightShoulder.z
    );
    vec3.normalize(sewCoordinates.body_frame.y_axis, sewCoordinates.body_frame.y_axis);

    return sewCoordinates;
  }

  private _getHandCentricCoordinates(handResults: HandLandmarkerResult): {
    left?: { landmarks: { [key: string]: vec3 }; confidence: number };
    right?: { landmarks: { [key: string]: vec3 }; confidence: number };
  } | null {
    if (!handResults.worldLandmarks || !this.humanSewPoses.left.W || !this.humanSewPoses.right.W ||
        !this.humanSewPoses.R_world_body) {
      return null;
    }

    const handFrames: {
      left?: { landmarks: { [key: string]: vec3 }; confidence: number };
      right?: { landmarks: { [key: string]: vec3 }; confidence: number };
    } = {};

    // Get body frame transformation matrix for converting to body-centric coordinates
    // Use the current body frame rotation matrix from pose processing
    const worldToBodyMatrix = mat3.transpose(mat3.create(), this.humanSewPoses.R_world_body);

    for (let handIdx = 0; handIdx < handResults.landmarks.length; handIdx++) {
      const handWorldLandmarks = handResults.worldLandmarks![handIdx];
      const handedness = handResults.handedness[handIdx];

      // Flip MediaPipe hand labels to match body pose perspective
      const mediapipeLabel = handedness[0].categoryName.toLowerCase();
      const actualHandLabel = mediapipeLabel === 'left' ? 'right' : 'left';

      // Get the corresponding pose wrist for alignment
      const poseWristBody = actualHandLabel === 'left' ? this.humanSewPoses.left.W! : this.humanSewPoses.right.W!;

      // Get hand wrist position
      const handWrist = handWorldLandmarks[HAND_LANDMARKS.WRIST];
      const handWristWorld = vec3.fromValues(handWrist.x, handWrist.y, handWrist.z);

      // Align all hand landmarks to pose world frame and convert to body frame
      const alignedHandLandmarks: { [key: string]: vec3 } = {};
      const landmarkNames = [
        'WRIST', 'THUMB_CMC', 'THUMB_MCP', 'THUMB_IP', 'THUMB_TIP',
        'INDEX_FINGER_MCP', 'INDEX_FINGER_PIP', 'INDEX_FINGER_DIP', 'INDEX_FINGER_TIP',
        'MIDDLE_FINGER_MCP', 'MIDDLE_FINGER_PIP', 'MIDDLE_FINGER_DIP', 'MIDDLE_FINGER_TIP',
        'RING_FINGER_MCP', 'RING_FINGER_PIP', 'RING_FINGER_DIP', 'RING_FINGER_TIP',
        'PINKY_MCP', 'PINKY_PIP', 'PINKY_DIP', 'PINKY_TIP'
      ];

      for (const landmarkName of landmarkNames) {
        const landmarkId = HAND_LANDMARKS[landmarkName as keyof typeof HAND_LANDMARKS];
        const handLandmark = handWorldLandmarks[landmarkId];

        // Get hand landmark in world coordinates
        const handLandmarkWorld = vec3.fromValues(handLandmark.x, handLandmark.y, handLandmark.z);

        // Align hand landmark to pose world frame (following original Python implementation):
        // Step 1: Translate hand landmark relative to hand wrist
        const relativeToHandWrist = vec3.subtract(vec3.create(), handLandmarkWorld, handWristWorld);

        // Step 2: Convert to body frame and add pose wrist position
        const relativeBody = vec3.transformMat3(vec3.create(), relativeToHandWrist, worldToBodyMatrix);
        const alignedBodyPos = vec3.add(vec3.create(), relativeBody, poseWristBody);

        alignedHandLandmarks[landmarkName.toLowerCase()] = alignedBodyPos;
      }

      // Store aligned hand information
      handFrames[actualHandLabel as 'left' | 'right'] = {
        landmarks: alignedHandLandmarks,
        confidence: handedness[0].score || 0.0
      };
    }

    return handFrames;
  }

  private _computeWristRotationFromHand(handSide: 'left' | 'right', handLandmarks: { [key: string]: vec3 }): mat3 | null {
    try {
      // Get key landmarks for defining wrist coordinate frame
      const wrist = handLandmarks['wrist'];
      const indexMcp = handLandmarks['index_finger_mcp'];
      const pinkyMcp = handLandmarks['pinky_mcp'];
      const ringMcp = handLandmarks['ring_finger_mcp'];
      const middleMcp = handLandmarks['middle_finger_mcp'];

      if (!wrist || !indexMcp || !pinkyMcp) {
        return null;
      }

      // Define hand coordinate frame with wrist as origin
      // X-axis: from wrist towards palm center (average of finger MCPs)
      let palmCenter: vec3;
      if (middleMcp && ringMcp) {
        palmCenter = vec3.fromValues(
          (indexMcp[0] + middleMcp[0] + ringMcp[0] + pinkyMcp[0]) / 4,
          (indexMcp[1] + middleMcp[1] + ringMcp[1] + pinkyMcp[1]) / 4,
          (indexMcp[2] + middleMcp[2] + ringMcp[2] + pinkyMcp[2]) / 4
        );
      } else if (middleMcp) {
        palmCenter = vec3.fromValues(
          (indexMcp[0] + middleMcp[0] + pinkyMcp[0]) / 3,
          (indexMcp[1] + middleMcp[1] + pinkyMcp[1]) / 3,
          (indexMcp[2] + middleMcp[2] + pinkyMcp[2]) / 3
        );
      } else {
        palmCenter = vec3.fromValues(
          (indexMcp[0] + pinkyMcp[0]) / 2,
          (indexMcp[1] + pinkyMcp[1]) / 2,
          (indexMcp[2] + pinkyMcp[2]) / 2
        );
      }

      const xAxis = vec3.subtract(vec3.create(), palmCenter, wrist);
      vec3.normalize(xAxis, xAxis);

      // Collect all available MCP points relative to wrist
      const mcpPoints: vec3[] = [];
      if (indexMcp) mcpPoints.push(vec3.subtract(vec3.create(), indexMcp, wrist));
      if (middleMcp) mcpPoints.push(vec3.subtract(vec3.create(), middleMcp, wrist));
      if (ringMcp) mcpPoints.push(vec3.subtract(vec3.create(), ringMcp, wrist));
      if (pinkyMcp) mcpPoints.push(vec3.subtract(vec3.create(), pinkyMcp, wrist));

      if (mcpPoints.length < 2) {
        return null; // Need at least 2 MCP points for reliable computation
      }

      // Use least squares to find Y-axis that's perpendicular to the palm plane
      // Convert vec3 array to matrix for SVD
      const A = new Matrix(mcpPoints.map(p => [p[0], p[1], p[2]]));

      // Find the normal to the plane containing MCP points using SVD
      const svd = new SingularValueDecomposition(A);
      const vMatrix = svd.rightSingularVectors;
      const palmNormalArray = vMatrix.getColumn(vMatrix.columns - 1); // Last column of V
      const palmNormal = vec3.fromValues(palmNormalArray[0], palmNormalArray[1], palmNormalArray[2]);
      vec3.normalize(palmNormal, palmNormal);

      // Determine handedness-consistent Y-axis direction
      const wristToIndex = vec3.subtract(vec3.create(), indexMcp, wrist);
      const wristToPinky = vec3.subtract(vec3.create(), pinkyMcp, wrist);

      // Cross product gives a reference direction
      const referenceY = vec3.cross(vec3.create(), wristToIndex, wristToPinky);
      vec3.normalize(referenceY, referenceY);

      // Choose palm normal direction that aligns with handedness
      if (vec3.dot(palmNormal, referenceY) < 0) {
        vec3.negate(palmNormal, palmNormal);
      }

      const yAxis = palmNormal;

      // Z-axis: Z = X × Y (ensuring right-handed coordinate system)
      const zAxis = vec3.cross(vec3.create(), xAxis, yAxis);
      vec3.normalize(zAxis, zAxis);

      // Create rotation matrix
      const rotationMatrix = mat3.fromValues(
        xAxis[0], yAxis[0], zAxis[0],
        xAxis[1], yAxis[1], zAxis[1],
        xAxis[2], yAxis[2], zAxis[2]
      );

      // Validate that it's a proper rotation matrix
      if (Math.abs(mat3.determinant(rotationMatrix) - 1.0) > 0.1) {
        if (this.debug) {
          console.warn(`Invalid rotation matrix computed for ${handSide} hand`);
        }
        return null;
      }

      return rotationMatrix;

    } catch (error) {
      if (this.debug) {
        console.error(`Error computing wrist rotation for ${handSide} hand:`, error);
      }
      return null;
    }
  }

  private _computeGraspFromHand(handSide: 'left' | 'right', handData: HandData): number {
    try {
      // Check if hand data is valid
      if (!handData.landmarks || handData.confidence < 0.5) {
        return 0.0; // Default to open gripper
      }

      const landmarks = handData.landmarks;

      // Get thumb tip and index finger tip positions
      const thumbTip = landmarks['thumb_tip'];
      const indexTip = landmarks['index_finger_tip'];

      if (!thumbTip || !indexTip) {
        return 0.0; // Default to open gripper
      }

      // Calculate distance between thumb and index finger tips
      const distance = vec3.distance(thumbTip, indexTip);

      // Grasp threshold - close gripper when distance < 0.04 meters (4 cm)
      const graspThreshold = 0.04;

      return distance < graspThreshold ? 1.0 : 0.0;

    } catch (error) {
      if (this.debug) {
        console.error(`Error computing grasp for ${handSide} hand:`, error);
      }
      return 0.0; // Default to open gripper on error
    }
  }

  private _computeTorsoAngles(R: mat3): vec3 {
    // Extract Euler angles (XYZ intrinsic) from rotation matrix
    // R = Rx(alpha) * Ry(beta) * Rz(gamma)
    // alpha = roll, beta = pitch, gamma = yaw
    
    // m02 = sin(beta)
    // m12 = -cos(beta) * sin(alpha)
    // m22 = cos(beta) * cos(alpha)
    // m01 = -cos(beta) * sin(gamma)
    // m00 = cos(beta) * cos(gamma)

    // gl-matrix is column-major:
    // 0 3 6
    // 1 4 7
    // 2 5 8
    
    const m02 = R[6];
    const m12 = R[7];
    const m22 = R[8];
    const m01 = R[3];
    const m00 = R[0];

    let alpha = 0; // Roll (X)
    let beta = 0;  // Pitch (Y)
    let gamma = 0; // Yaw (Z)

    if (Math.abs(m02) < 0.99999) {
      beta = Math.asin(m02);
      alpha = Math.atan2(-m12, m22);
      gamma = Math.atan2(-m01, m00);
    } else {
      // Gimbal lock case
      // If beta = pi/2, m02 = 1, cos(beta) = 0
      // R = [ 0  0  1 ]
      //     [ s(a+g) c(a+g) 0 ]
      //     [ -c(a+g) s(a+g) 0 ]
      // We can only find alpha + gamma. Set gamma = 0.
      beta = Math.sign(m02) * Math.PI / 2;
      alpha = Math.atan2(R[1], R[4]); // atan2(m10, m11)
      gamma = 0;
    }

    // User requested: [yaw, roll, pitch]
    // yaw = gamma, roll = alpha, pitch = beta
    return vec3.fromValues(gamma, alpha, beta);
  }

  private _checkEngagement(): void {
    // Check if both arms have valid SEW data
    const leftSew = this.humanSewPoses.left;
    const rightSew = this.humanSewPoses.right;

    const leftEngaged = leftSew.S !== null && leftSew.E !== null && leftSew.W !== null;
    const rightEngaged = rightSew.S !== null && rightSew.E !== null && rightSew.W !== null;

    // Require both arms to be visible and tracked for engagement
    this.engaged = leftEngaged && rightEngaged;
  }

  // Public API methods

  startControl(): void {
    this._resetInternalState();
    this.engaged = true;
  }

  getControllerState(): ControllerState {
    return this.controllerState || {
      sew_poses: this._cloneSewPoses(this.humanSewPoses),
      wrist_poses: this._cloneWristPoses(this.humanWristPoses),
      hand_poses: this._cloneHandPoses(this.humanHandPoses),
      torso_angles: null,
      engaged: this.engaged
    };
  }

  getSewAndWristPoses(): { sew_poses: SEWPoses; wrist_poses: WristPoses } {
    return {
      sew_poses: this._cloneSewPoses(this.humanSewPoses),
      wrist_poses: this._cloneWristPoses(this.humanWristPoses)
    };
  }

  getLatestPoseResults(): PoseLandmarkerResult | null {
    return this.latestPoseResults;
  }

  getLatestHandResults(): HandLandmarkerResult | null {
    return this.latestHandResults;
  }

  close(): void {
    if (this.poseLandmarker) {
      this.poseLandmarker.close();
      this.poseLandmarker = undefined;
    }
    if (this.handLandmarker) {
      this.handLandmarker.close();
      this.handLandmarker = undefined;
    }
  }

  // Utility methods

  private _cloneSEWPose(pose: SEWPose): SEWPose {
    return {
      S: pose.S ? vec3.clone(pose.S) : null,
      E: pose.E ? vec3.clone(pose.E) : null,
      W: pose.W ? vec3.clone(pose.W) : null
    };
  }

  private _cloneSewPoses(poses: SEWPoses): SEWPoses {
    return {
      left: this._cloneSEWPose(poses.left),
      right: this._cloneSEWPose(poses.right),
      R_world_body: poses.R_world_body ? mat3.clone(poses.R_world_body) : null
    };
  }

  private _cloneWristPoses(poses: WristPoses): WristPoses {
    return {
      left: poses.left ? mat3.clone(poses.left) : null,
      right: poses.right ? mat3.clone(poses.right) : null
    };
  }

  private _cloneHandData(data: HandData): HandData {
    const landmarks: { [key: string]: vec3 } | null = data.landmarks ? {} : null;
    if (landmarks && data.landmarks) {
      for (const key in data.landmarks) {
        landmarks[key] = vec3.clone(data.landmarks[key]);
      }
    }
    return {
      landmarks,
      confidence: data.confidence
    };
  }

  private _cloneHandPoses(poses: HandPoses): HandPoses {
    return {
      left: this._cloneHandData(poses.left),
      right: this._cloneHandData(poses.right)
    };
  }

  private _copyObject<T>(obj: T): T {
    // Use structuredClone if available, otherwise fallback to JSON (though slower)
    if (typeof structuredClone === 'function') {
      return structuredClone(obj);
    }
    return JSON.parse(JSON.stringify(obj));
  }
}
